<div class="ingestor-tenant-mapping mb-3">
  <div class="mapping-header">
    <h2>{{ title() }}</h2>
    <button
      kendoButton
      fillMode="flat"
      [svgIcon]="xIcon"
      title="Close"
      (click)="closeMapping()"
    ></button>
  </div>

  @if (recordState() === 'missing') {
    <div
      class="alert alert-warning mb-3"
      role="alert"
    >
      {{ missingRecordMessage() }}
    </div>
  }

  <div class="mapping-toolbar mb-3">
    <button
      kendoButton
      themeColor="primary"
      [svgIcon]="plusIcon"
      [disabled]="!canEdit()"
      (click)="openAddDialog()"
    >
      Add Mapping
    </button>
    <button
      kendoButton
      [svgIcon]="fileCsvIcon"
      [disabled]="!canEdit() || downloading() === 'template'"
      (click)="downloadCsv('template')"
    >
      Download Template
    </button>
    <button
      kendoButton
      [svgIcon]="downloadIcon"
      [disabled]="!canEdit() || downloading() === 'export'"
      (click)="downloadCsv('export')"
    >
      Export CSV
    </button>
    <button
      kendoButton
      [svgIcon]="uploadIcon"
      [disabled]="!canEdit()"
      (click)="openUploadDialog()"
    >
      Upload CSV
    </button>
  </div>

  @if (listUrl()) {
    <app-datatable
      #datatable
      [data-href]="listUrl()"
      [columns]="columns()"
      [successCallback]="onRowsLoaded"
      [useOData]="true"
      (click)="onTableClick($event)"
    ></app-datatable>
  }
</div>

@if (dialogMode(); as mode) {
  <kendo-dialog
    [title]="mode === 'add' ? 'Add Tenant Mapping' : 'Edit Tenant Mapping'"
    (close)="closeDialog()"
    [minWidth]="500"
    [width]="600"
  >
    <form
      class="dialog-content"
      [formGroup]="form"
      (ngSubmit)="saveMapping()"
    >
      @for (message of generalErrors(); track message) {
        <div
          class="alert alert-danger mb-3"
          role="alert"
        >
          {{ message }}
        </div>
      }

      <div class="form-group mb-3">
        <label class="form-label required">Organization</label>
        <cyflare-autocomplete
          [baseUrl]="'v1/organization/administrative/organizationautocomplete/'"
          [textField]="'display_text'"
          [valueField]="'id'"
          [placeholder]="'Search organization...'"
          [showDropdown]="true"
          [completeOnFocus]="false"
          [pageSize]="100"
          [extraQueryParams]="'parent_id=' + organizationId()"
          [defaultItem]="organizationDefault()"
          [selectedIds]="form.controls.organization.value?.toString() || ''"
          (itemSelected)="onOrganizationSelected($event)"
          (itemCleared)="onOrganizationCleared()"
        ></cyflare-autocomplete>
        @for (message of errorsFor('organization'); track message) {
          <div class="field-error">{{ message }}</div>
        }
      </div>

      <div class="form-group mb-3">
        <label
          class="form-label required"
          for="tenant-map-external-id"
        >
          {{ idLabel() }}
        </label>
        <kendo-textbox
          id="tenant-map-external-id"
          formControlName="integration_id"
          [placeholder]="idLabel()"
          (valueChange)="clearFieldError('integration_id')"
        ></kendo-textbox>
        @if (form.controls.integration_id.touched && form.controls.integration_id.invalid) {
          <div class="field-error">{{ idLabel() }} is required.</div>
        }
        @for (message of errorsFor('integration_id'); track message) {
          <div class="field-error">{{ message }}</div>
        }
      </div>

      <div class="form-group mb-3">
        <label
          class="form-label"
          for="tenant-map-external-name"
        >
          {{ nameLabel() }}
        </label>
        <kendo-textbox
          id="tenant-map-external-name"
          formControlName="integration_unique_name"
          placeholder="Defaults to the organization name"
          (valueChange)="clearFieldError('integration_unique_name')"
        ></kendo-textbox>
        @for (message of errorsFor('integration_unique_name'); track message) {
          <div class="field-error">{{ message }}</div>
        }
      </div>
    </form>

    <kendo-dialog-actions>
      <button
        kendoButton
        (click)="closeDialog()"
      >
        Cancel
      </button>
      <button
        kendoButton
        themeColor="primary"
        [disabled]="form.invalid || saving()"
        (click)="saveMapping()"
      >
        Save
      </button>
    </kendo-dialog-actions>
  </kendo-dialog>
}

@if (deleting(); as row) {
  <kendo-dialog
    title="Delete Tenant Mapping"
    (close)="closeDeleteDialog()"
    [minWidth]="400"
    [width]="500"
  >
    <div class="dialog-content">
      <p>Are you sure you want to delete this tenant mapping?</p>
      <div class="mapping-details">
        <strong>Organization:</strong>
        {{ row.organization.name }}
        <br />
        <strong>{{ idLabel() }}:</strong>
        {{ row.integration_id }}
      </div>
    </div>

    <kendo-dialog-actions>
      <button
        kendoButton
        (click)="closeDeleteDialog()"
      >
        Cancel
      </button>
      <button
        kendoButton
        themeColor="error"
        [disabled]="deleteBusy()"
        (click)="confirmDelete()"
      >
        Delete
      </button>
    </kendo-dialog-actions>
  </kendo-dialog>
}

@if (uploadOpen()) {
  <kendo-dialog
    title="Upload Tenant Mappings CSV"
    (close)="closeUploadDialog()"
    [minWidth]="500"
    [width]="700"
  >
    <div class="dialog-content">
      <p class="csv-hint">
        Expected columns:
        <code>{{ csvColumns() }}</code>
        (the last one is optional). Rows with a blank {{ idLabel() }} are skipped.
      </p>

      <kendo-fileselect
        [restrictions]="csvRestrictions"
        [multiple]="false"
        (valueChange)="onCsvFileChange($event)"
      ></kendo-fileselect>

      <div class="csv-dry-run mt-3">
        <input
          kendoCheckBox
          type="checkbox"
          id="tenant-map-dry-run"
          [checked]="dryRun()"
          (change)="dryRun.set($any($event.target).checked)"
        />
        <kendo-label
          for="tenant-map-dry-run"
          text="Validate only (dry run)"
        ></kendo-label>
      </div>

      @if (uploadError(); as message) {
        <div
          class="alert alert-danger mt-3 mb-0"
          role="alert"
        >
          {{ message }}
        </div>
      }

      @if (uploadOutcome(); as outcome) {
        @if (outcome.rejected) {
          <div
            class="alert alert-danger mt-3"
            role="alert"
          >
            Nothing was imported. Fix the rows below and upload the file again.
          </div>
          <table class="table table-sm csv-errors mb-0">
            <thead>
              <tr>
                <th>Row</th>
                <th>Organization ID</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              @for (error of outcome.result.errors; track $index) {
                <tr>
                  <td>{{ error.row }}</td>
                  <td>{{ error.organization_id }}</td>
                  <td>{{ error.message }}</td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <div
            class="alert mt-3"
            [class.alert-info]="outcome.dryRun"
            [class.alert-success]="!outcome.dryRun"
            role="status"
          >
            {{ outcome.dryRun ? 'Dry run complete. Nothing was saved.' : 'Import complete.' }}
          </div>
          <div class="csv-counters">
            <div class="csv-counter">
              <span class="csv-counter-label">Created</span>
              <strong class="csv-counter-value">{{ outcome.result.created }}</strong>
            </div>
            <div class="csv-counter">
              <span class="csv-counter-label">Updated</span>
              <strong class="csv-counter-value">{{ outcome.result.updated }}</strong>
            </div>
            <div class="csv-counter">
              <span class="csv-counter-label">Unchanged</span>
              <strong class="csv-counter-value">{{ outcome.result.unchanged }}</strong>
            </div>
            <div class="csv-counter">
              <span class="csv-counter-label">Skipped (blank)</span>
              <strong class="csv-counter-value">{{ outcome.result.skipped_blank }}</strong>
            </div>
          </div>
        }
      }
    </div>

    <kendo-dialog-actions>
      <button
        kendoButton
        (click)="closeUploadDialog()"
      >
        Close
      </button>
      <button
        kendoButton
        themeColor="primary"
        [disabled]="!csvFile() || uploading()"
        (click)="uploadCsv()"
      >
        Upload
      </button>
    </kendo-dialog-actions>
  </kendo-dialog>
}
